import { ArrowRight, Building2, CalendarDays, Layers, Send, Trash2 } from 'lucide-react';
import {
  type SupplyReportDto,
} from '@eombor/contracts';
import { Badge, Button, Card, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { supplyStatusText } from '../../../shared/i18n/labels.js';

/**
 * TUSHUM KARTOCHKASI.
 *
 * Jadval o'rniga kartochka, chunki bu ro'yxatning savoli "ustunlarni
 * solishtirish" emas: skladchi bitta narsani bilmoqchi — QAYSI hujjatda
 * ish qolgan. Shuning uchun kartochkaning markazida bajarilish chizig'i
 * turadi, qolgani esa uni tushuntiradigan ikkinchi darajali matn.
 *
 * O'QILMAGAN hujjat chap chetidagi rangli chiziq bilan ajraladi. Nuqta
 * yoki qalin shrift emas: ro'yxat uzun bo'lganda chekka chiziq ko'z
 * yugurtirganda ham ko'rinadi.
 */

const tone = (status: SupplyReportDto['status']): 'warn' | 'info' | 'success' | 'neutral' => {
  if (status === 'DRAFT') return 'neutral';
  if (status === 'SENT') return 'warn';
  if (status === 'PARTIAL') return 'info';
  return status === 'ACCEPTED' ? 'success' : 'neutral';
};

export interface SupplyCardProps {
  report: SupplyReportDto;
  warehouseName: string;
  /** Bu hujjat bo'yicha o'qilmagan xabar bormi. */
  unread: boolean;
  onOpen: () => void;
  /**
   * Qoralamani yuborish va o'chirish — FAQAT yuklovchida.
   *
   * Berilmasa tugmalar chizilmaydi: skladchi qoralamani umuman
   * ko'rmaydi, ko'rgan taqdirda ham unga bu tugmalar tegishli emas.
   */
  onSend?: () => void;
  onDelete?: () => void;
  busy?: boolean;
}

export const SupplyCard = ({
  report,
  warehouseName,
  unread,
  onOpen,
  onSend,
  onDelete,
  busy = false,
}: SupplyCardProps) => {
  /* O'lchov — ISH BOR satrlar. Qoldig'i nol satr joylashtirilmaydi:
     u maxrajda qolsa chiziq to'lmas, «qolgani» esa nolga tushmasdi. */
  const total = Math.max(report.workLineCount, 1);
  const percent = Math.round((report.placedLineCount / total) * 100);
  /*
   * BOSHLANGAN ish ham ko'rinadi. Ilgari chiziq faqat TUGAGAN satrlarni
   * ko'rsatardi va omborchi o'ttizta satrning yarmini qo'yganda ham
   * qimirlamasdi — «joylashtirdim-ku, nega to'lamayapti» degan savol
   * shundan chiqardi. Endi ikkinchi, ochroq bo'lak tegilgan satrlarni
   * ko'rsatadi: bitta narsa qo'yilsa ham chiziq darrov javob beradi.
   *
   * Ikkisi QO'SHILMAYDI, yonma-yon turadi: hujjat qachon yopilishini
   * baribir faqat tugagan satrlar hal qiladi va ularning soni
   * sarlavhadagi `60 / 162` bilan bir xil bo'lib qolishi kerak.
   */
  const startedPercent = Math.round((report.startedLineCount / total) * 100);
  const left = report.workLineCount - report.placedLineCount;
  const untouched = left - report.startedLineCount;
  const isDraft = report.status === 'DRAFT';

  return (
    <Card
      className={cn(
        'flex flex-col gap-4 border-l-4 transition-colors',
        unread ? 'border-l-[var(--color-accent)]' : 'border-l-transparent',
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-text font-mono text-sm font-semibold">{report.docNo}</span>
            <Badge tone={tone(report.status)}>{supplyStatusText(report.status)}</Badge>
            {unread ? <Badge tone="warn">{t('supply.new')}</Badge> : null}
          </div>
          <span className="text-text-muted flex items-center gap-1.5 text-xs">
            <Building2 size={13} aria-hidden />
            {warehouseName}
          </span>
        </div>
        <Button variant="secondary" onClick={onOpen}>
          {t('supply.open')}
          <ArrowRight size={14} aria-hidden />
        </Button>
      </header>

      <div className="flex flex-col gap-1.5">
        <div className="text-text-muted flex items-baseline justify-between text-xs">
          <span>{t('supply.progress')}</span>
          <span className="tabular text-text">
            {report.placedLineCount} / {report.workLineCount}
          </span>
        </div>
        {/* Chiziqning O'ZI ma'lumot: rang holatdan emas, bajarilishdan keladi. */}
        <div
          className="bg-surface-2 flex h-1.5 w-full overflow-hidden rounded-full"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('supply.progress')}
        >
          {/* TUGAGAN — to'q rang. Ish bitgach yashil bo'ladi: ilgari bu
              yerda `--color-fill-empty` turardi va u chiziqning orqa
              foni bilan deyarli bir xil edi, ya'ni 100% bajarilgan
              hujjat eng BO'SH ko'rinardi. */}
          <div
            className={cn(
              'h-full transition-[width]',
              left === 0 ? 'bg-success' : 'bg-accent',
            )}
            style={{ width: `${percent}%` }}
          />
          {/* BOSHLANGAN — o'sha rangning ochrog'i. Alohida token emas,
              shaffoflik: ikki bo'lak bitta ishning ikki holati ekani
              rangning o'zidan ko'rinib tursin. */}
          <div
            className="bg-accent/35 h-full transition-[width]"
            style={{ width: `${startedPercent}%` }}
          />
        </div>

        {/* Chiziqni SO'Z bilan ham aytamiz: rang nimani anglatishini
            taxmin qilish omborchining ishi emas. */}
        <p className="text-text-muted text-xs">
          {left === 0
            ? t('supply.progressDone')
            : t('supply.progressParts', {
                done: report.placedLineCount,
                started: report.startedLineCount,
                left: untouched,
              })}
        </p>
      </div>

      <dl className="text-text-muted grid grid-cols-3 gap-2 text-xs">
        <div className="flex flex-col gap-0.5">
          <dt className="flex items-center gap-1">
            <Layers size={12} aria-hidden />
            {t('supply.suppliers')}
          </dt>
          <dd className="text-text tabular">{report.supplierCount}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt>{t('supply.colPeriod')}</dt>
          <dd className="text-text truncate">{report.periodLabel ?? '—'}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="flex items-center gap-1">
            <CalendarDays size={12} aria-hidden />
            {t('supply.colDate')}
          </dt>
          <dd className="text-text tabular">{report.createdAt.slice(0, 10)}</dd>
        </div>
      </dl>

      {/* QORALAMA — hali hech kimga ketmagan. Ikki tugma: yuborish yoki
          o'chirish. Yuborilgandan keyin ikkalasi ham yo'qoladi. */}
      {isDraft && (onSend !== undefined || onDelete !== undefined) ? (
        <footer className="border-border flex flex-col gap-2 border-t pt-3">
          <p className="text-text-muted text-xs">{t('supply.draftHint')}</p>
          <div className="flex flex-wrap gap-2">
            {onSend === undefined ? null : (
              <Button onClick={onSend} disabled={busy}>
                <Send size={14} aria-hidden />
                {t('supply.sendToKeeper')}
              </Button>
            )}
            {onDelete === undefined ? null : (
              <Button variant="ghost" onClick={onDelete} disabled={busy}>
                <Trash2 size={14} aria-hidden />
                {t('supply.delete')}
              </Button>
            )}
          </div>
        </footer>
      ) : null}
    </Card>
  );
};
